import type { FAQPage, Question } from 'schema-dts';

import { stripHtml } from '../textUtils';
import type { NonStringThing } from './structuredData.types';
import {
  STORYBLOCKS_ORG,
  STORYBLOCKS_PUBLISHER_REF,
} from './structuredDataConstants';

export interface FAQItemInput {
  faqQuestion: string;
  faqAnswer: string;
}

export interface FAQSchemaGeneratorProps {
  faqItems: FAQItemInput[];
  title?: string;
  description?: string;
  url?: string;
  organizationName?: string;
}

const validateFAQItem = (item: FAQItemInput, index: number): void => {
  if (process.env.NODE_ENV !== 'production') {
    if (!item.faqQuestion || item.faqQuestion.trim() === '') {
      console.warn(
        `FAQ item ${index + 1} missing required question property:`,
        item
      );
    }

    if (!item.faqAnswer || item.faqAnswer.trim() === '') {
      console.warn(
        `FAQ item ${index + 1} missing required answer property:`,
        item
      );
    }
  }
};

const validateFAQSchema = (schema: FAQPage): void => {
  if (process.env.NODE_ENV !== 'production') {
    if (!schema['@type']) {
      console.warn(
        'Generated FAQ schema missing required @type property:',
        schema
      );
    }

    if (
      !schema.mainEntity ||
      !Array.isArray(schema.mainEntity) ||
      schema.mainEntity.length === 0
    ) {
      console.warn(
        'FAQ schema missing required mainEntity property with questions:',
        schema
      );
    }
  }
};

const getOrganizationReference = (organizationName?: string) => {
  if (organizationName === 'Storyblocks') {
    return STORYBLOCKS_ORG;
  }
  return organizationName
    ? {
        '@type': 'Organization' as const,
        name: organizationName,
      }
    : undefined;
};

const getPublisherReference = (organizationName?: string) => {
  if (organizationName === 'Storyblocks') {
    return STORYBLOCKS_PUBLISHER_REF.publisher;
  }
  return organizationName
    ? {
        '@type': 'Organization' as const,
        name: organizationName,
      }
    : undefined;
};

const generateQuestionItem = (item: FAQItemInput, index: number): Question => {
  validateFAQItem(item, index);

  const question: Question = {
    '@type': 'Question',
    name: stripHtml(item.faqQuestion),
    acceptedAnswer: {
      '@type': 'Answer',
      text: stripHtml(item.faqAnswer),
    },
  };

  return question;
};

/**
 * Generate FAQ structured data following schema.org FAQPage specification
 *
 * @param config Configuration object containing FAQ items and metadata
 * @returns FAQPage structured data object ready for JSON-LD serialization, or null if no items
 */
export const generateFAQSchema = ({
  faqItems,
  title = 'Frequently Asked Questions',
  description,
  url,
  organizationName,
}: FAQSchemaGeneratorProps): NonStringThing | null => {
  if (!faqItems?.length) {
    return null;
  }

  const validFAQItems = faqItems.filter(
    (item) =>
      item.faqQuestion &&
      item.faqQuestion.trim() !== '' &&
      item.faqAnswer &&
      item.faqAnswer.trim() !== ''
  );

  if (validFAQItems.length === 0) {
    return null;
  }

  const questions = validFAQItems.map((item, index) =>
    generateQuestionItem(item, index)
  );

  const faqPage: FAQPage = {
    '@type': 'FAQPage',
    name: title,
    ...(description && { description: stripHtml(description) }),
    ...(url && { url }),
    mainEntity: questions,
  };

  const publisher = getPublisherReference(organizationName);
  if (publisher) {
    faqPage.publisher = publisher;
  }

  const organization = getOrganizationReference(organizationName);
  if (organization) {
    faqPage.about = organization;
  }

  validateFAQSchema(faqPage);
  return faqPage;
};
